import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const linkClass = `text-sm font-semibold whitespace-nowrap transition-colors ${
    isTransparent
      ? "text-white/90 hover:text-white"
      : "text-foreground-800 hover:text-foreground-950"
  }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent
            ? "bg-transparent"
            : "bg-background-50/98 backdrop-blur-md border-b border-background-200/70"
        }`}
      >
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link
                to="/"
                className={`font-heading text-xl font-bold whitespace-nowrap ${
                  isTransparent ? "text-white" : "text-foreground-950"
                }`}
              >
                itaobuy<span className="font-light">.cfd</span>
              </Link>

              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className={linkClass}>
                  Home
                </Link>
                <a href="#categories" className={linkClass}>
                  Categories
                </a>
                <div ref={dropdownRef} className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`flex items-center gap-1 ${linkClass}`}
                  >
                    Shop by Category
                    <i className={`ri-arrow-down-s-line transition-transform w-4 h-4 flex items-center justify-center ${dropdownOpen ? "rotate-180" : ""}`}></i>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                      {categories.map((cat, idx) => (
                        <a
                          key={idx}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <Link to="/shopping-guide" className={linkClass}>
                  Shopping Guide
                </Link>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  isTransparent
                    ? "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                }`}
              >
                itaobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  isTransparent
                    ? "bg-white text-foreground-950 hover:bg-white/90"
                    : "bg-foreground-950 text-white hover:bg-foreground-800"
                }`}
              >
                buy it now
                <i className="ri-arrow-right-up-line text-sm w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isTransparent
                  ? "text-white hover:bg-white/10"
                  : "text-foreground-950 hover:bg-background-100"
              }`}
              aria-label="Open navigation menu"
            >
              <i className="ri-menu-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-background-50 md:hidden">
          <div className="flex items-center justify-between px-6 h-16 border-b border-background-200/70">
            <Link
              to="/"
              className="font-heading text-xl font-bold text-foreground-950"
              onClick={() => setMobileMenuOpen(false)}
            >
              itaobuy<span className="font-light">.cfd</span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-2xl text-foreground-950 w-6 h-6 flex items-center justify-center"></i>
            </button>
          </div>

          <div className="px-6 py-8 overflow-y-auto h-[calc(100vh-64px)]">
            <div className="space-y-5">
              <Link
                to="/"
                className="block text-lg font-semibold text-foreground-950"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line mr-3 w-5 h-5 inline-flex items-center justify-center"></i>
                Home
              </Link>
              <a
                href="#categories"
                className="block text-lg font-semibold text-foreground-950"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-grid-line mr-3 w-5 h-5 inline-flex items-center justify-center"></i>
                Categories
              </a>
              <Link
                to="/shopping-guide"
                className="block text-lg font-semibold text-foreground-950"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-book-open-line mr-3 w-5 h-5 inline-flex items-center justify-center"></i>
                Shopping Guide
              </Link>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Shop by Category
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-sm text-foreground-700 hover:text-foreground-950 transition-colors py-1.5"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary-500 text-white rounded-lg text-sm font-semibold whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-foreground-950 text-white rounded-lg text-sm font-semibold whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}